/**
 * The status palettes the tutor panel renders chips from.
 *
 * These maps used to be re-declared at the top of TutorClasses, TutorResources, TutorRecordings and
 * TutorGroups, which meant the dashboard could not show a status breakdown without a fifth copy.
 * The values are unchanged — this file is only where they now live.
 */

/** Classroom lifecycle, as ClassroomController reports it. */
export const classStatusChip = {
  pending: { label: "Pending", color: "#ff9800" },
  ongoing: { label: "Ongoing", color: "#4caf50" },
  ended: { label: "Ended", color: "#9e9e9e" },
};

/**
 * Content review stage. A tutor assistant's upload waits for the group's lead tutor, then for an
 * admin — only the admin's verdict makes a file visible to a student.
 */
export const resourceStageChip = {
  awaiting_tutor: { label: "Awaiting lead tutor", color: "#ed6c02", bg: "#ed6c0218" },
  rejected_by_tutor: { label: "Rejected by lead tutor", color: "#d32f2f", bg: "#d32f2f18" },
  awaiting_admin: { label: "Awaiting admin", color: "#0288d1", bg: "#0288d118" },
  approved: { label: "Visible to students", color: "#2e7d32", bg: "#2e7d3218" },
  completed: { label: "Complete", color: "#2e7d32", bg: "#2e7d3218" },
  rejected: { label: "Not approved", color: "#d32f2f", bg: "#d32f2f18" },
};

/** Older rows that carry only an approval_status, with no stage. */
export const resourceStatusChip = {
  pending: { label: "Awaiting review", color: "#ed6c02", bg: "#ed6c0218" },
  approved: { label: "Visible to students", color: "#2e7d32", bg: "#2e7d3218" },
  rejected: { label: "Not approved", color: "#d32f2f", bg: "#d32f2f18" },
};

export const resourceChipFor = (r) =>
  resourceStageChip[r.stage] || resourceStatusChip[r.approval_status] || resourceStatusChip.pending;

/** Who uploaded it. Admin rows are course material a tutor can read but not delete. */
export const RESOURCE_SOURCE_LABEL = {
  admin: { label: "Admin", color: "#1565c0", bg: "#1565c018" },
  tutor: { label: "Lead tutor", color: "#2e7d32", bg: "#2e7d3218" },
  assistant: { label: "Assistant", color: "#6a1b9a", bg: "#6a1b9a18" },
};

/** Cloudflare Stream upload/encode state for a class recording. */
export const recordingStatusChip = {
  pending: { label: "Not uploaded", color: "#6b7280", bg: "#6b728018" },
  uploading: { label: "Uploading", color: "#ed6c02", bg: "#ed6c0218" },
  processing: { label: "Processing", color: "#0288d1", bg: "#0288d118" },
  ready: { label: "Available to students", color: "#2e7d32", bg: "#2e7d3218" },
  failed: { label: "Failed", color: "#d32f2f", bg: "#d32f2f18" },
};

/** Lead tutor vs tutor assistant — the two roles in course_plan_staff (stored value 'helper'). */
export const groupRoleConfig = {
  tutor: { label: "Lead tutor", color: "#2e7d32" },
  helper: { label: "Tutor Assistant", color: "#0277bd" },
};

/**
 * How to name a staff member's role on a group, given what they hold.
 *
 * Someone with subjects is that subject's tutor and teaches it in their own right, so calling them
 * an "assistant" understates them. The word is kept only for staff with nothing assigned, where it
 * is accurate: they upload, the lead approves, and they cannot host. The stored role value is
 * unchanged either way — this is presentation.
 */
export function staffRoleLabel(role, subjects = [], { short = false } = {}) {
  if (role === "tutor") return "Lead tutor";
  if (subjects.length === 0) return "Tutor Assistant";
  // `short` is for places that already list the subjects nearby, where naming them again in the
  // chip only makes it long enough to squeeze whatever sits beside it.
  return short ? "Subject tutor" : `${subjects.map((s) => s.name).join(" & ")} tutor`;
}
